// item 37 — the whole app shell is auth-gated. This is the PURE decision behind that
// gate, extracted from App.tsx so the security-critical "never render the app to an
// unauthenticated visitor" rule is unit-tested, not just asserted in a component.
//
//  - loading  → Auth0 hasn't resolved the session yet: show a neutral spinner, do NOT
//               render the app and do NOT redirect (we don't know yet).
//  - denied   → Auth0 resolved WITH an error (e.g. access_denied from an invitation-only
//               tenant rule): show a failed-login screen. Auto-redirecting here loops
//               forever — Auth0 silently reuses the denied session and denies again
//               (item 984, Chad's spinning/flashing Custom Builder login).
//  - redirect → resolved AND not signed in: send them to the Auth0 login.
//  - app      → resolved AND signed in: render the chrome.

export type AuthGate = 'loading' | 'denied' | 'redirect' | 'app';

export const authGateDecision = (
  isLoading: boolean, isAuthenticated: boolean, hasAuthError = false,
): AuthGate => {
  if (isLoading) return 'loading';
  if (isAuthenticated) return 'app';
  if (hasAuthError) return 'denied';
  return 'redirect';
};

// the app chrome renders ONLY when authenticated and resolved — never otherwise.
export const shouldRenderApp = (
  isLoading: boolean, isAuthenticated: boolean, hasAuthError = false,
): boolean => (
  authGateDecision(isLoading, isAuthenticated, hasAuthError) === 'app'
);

// kick off the Auth0 login redirect only once we KNOW the visitor is unauthenticated —
// and never on a denied login (that's the infinite bounce).
export const shouldRedirectToLogin = (
  isLoading: boolean, isAuthenticated: boolean, hasAuthError = false,
): boolean => (
  authGateDecision(isLoading, isAuthenticated, hasAuthError) === 'redirect'
);

// the failed-login screen (item 984): resolved, unauthenticated, with an Auth0 error.
export const shouldShowLoginFailed = (
  isLoading: boolean, isAuthenticated: boolean, hasAuthError: boolean,
): boolean => (
  authGateDecision(isLoading, isAuthenticated, hasAuthError) === 'denied'
);
